import React from "react";
import { SQSProcessName } from "@common/constants/process-names";
import {
    AuditLog,
    AuditLogChangeType,
    AuditLogDiff,
} from "@common/types/auditLog";
import { toAccrualLineArray } from "@common/helpers/accrualLineDeltas";
import { sortLineItems } from "@common/helpers/lineItems";
import { pluralizeWithCount } from "@common/helpers/string";
import { ACCRUAL_MATCH_AUDIT_FIELD } from "@common/schemas/domains/accrualReview/accrual-match-audit-log.schema";
import { parseAccrualMatchSnapshot } from "@components/history/details/accrualMatches/schema";
import {
    AccrualLabelResolver,
    describeDiffSide,
    getAccrualCurrency,
    getAccrualFieldLabel,
    getLineFieldSummaries,
    getLineLabel,
} from "./accrualValueFormat";
import { joinPhrases, renderFieldPhrase } from "./accrualActivityPhrases";
import { describeLineDeltas } from "./accrualLineActivitySummary";
import styles from "./AccrualActivity.module.scss";

const LINES_FIELD = "lines";

const MAX_LISTED_LINE_ROWS = 10;

const HIDDEN_FIELDS: ReadonlySet<string> = new Set([
    "id",
    "entityId",
    ACCRUAL_MATCH_AUDIT_FIELD,
]);

export type AccrualEntityLabel = "bill" | "invoice";

/**
 * A sentence that follows the author, plus an optional breakdown underneath.
 * One stored audit log can produce several of these: a save that changes
 * several line items becomes one row per line, the same shape as a save that
 * touched a single line. The activity header still counts stored logs.
 */
interface AccrualActivityContent {
    /** Predicate rendered after the author, e.g. `created this invoice.` */
    summary: React.ReactNode;
    details?: React.ReactNode;
}

function splitDiffs(log: AuditLog): {
    lineDiff: AuditLogDiff | undefined;
    fieldDiffs: AuditLogDiff[];
} {
    const diffs = log.diff.filter((diff) => !HIDDEN_FIELDS.has(diff.field));
    return {
        lineDiff: diffs.find((diff) => diff.field === LINES_FIELD),
        fieldDiffs: diffs.filter((diff) => diff.field !== LINES_FIELD),
    };
}

function buildMatchContent({
    entityLabel,
    log,
}: {
    entityLabel: AccrualEntityLabel;
    log: AuditLog;
}): AccrualActivityContent {
    const diff = log.diff.find((d) => d.field === ACCRUAL_MATCH_AUDIT_FIELD);
    const matched = (diff?.after?.value ?? null) !== null;
    const snapshot = parseAccrualMatchSnapshot(
        matched ? diff?.after?.value : diff?.before?.value,
    );
    const target = snapshot ? <strong>a transaction</strong> : null;

    if (matched) {
        return {
            summary: (
                <>
                    matched this {entityLabel}
                    {target ? <> with {target}</> : null}.
                </>
            ),
        };
    }

    return {
        summary: (
            <>
                unmatched this {entityLabel}
                {target ? <> from {target}</> : null}.
            </>
        ),
    };
}

function renderDeletedLines({
    diff,
    resolver,
}: {
    diff: AuditLogDiff;
    resolver: AccrualLabelResolver;
}): React.ReactNode {
    const lines = sortLineItems(toAccrualLineArray(diff.before.value));
    if (lines.length === 0) {
        return null;
    }

    return (
        <ul className={styles.lineDeltaList}>
            {lines.map((line, index) => (
                <li key={line.id} className={styles.lineDeltaItem}>
                    <span className={styles.lineHeader}>
                        Removed line item {getLineLabel(line, index)}
                    </span>
                    <ul className={styles.lineFieldList}>
                        {getLineFieldSummaries(line, resolver).map(
                            ({ field, label, text }) => (
                                <li
                                    key={field}
                                    className={styles.lineFieldItem}
                                >
                                    <span className={styles.lineFieldLabel}>
                                        {label}
                                    </span>
                                    <span className={styles.before}>
                                        {text}
                                    </span>
                                </li>
                            ),
                        )}
                    </ul>
                </li>
            ))}
        </ul>
    );
}

function renderDeletedSnapshotDetails({
    log,
    resolver,
}: {
    log: AuditLog;
    resolver: AccrualLabelResolver;
}): React.ReactNode {
    const currency = getAccrualCurrency(log);
    const { lineDiff, fieldDiffs } = splitDiffs(log);
    const lineDetails = lineDiff
        ? renderDeletedLines({ diff: lineDiff, resolver })
        : null;

    if (fieldDiffs.length === 0 && lineDetails === null) {
        return null;
    }

    return (
        <>
            {fieldDiffs.length > 0 && (
                <ul className={styles.lineFieldList}>
                    {fieldDiffs.map((diff) => (
                        <li key={diff.field} className={styles.lineFieldItem}>
                            <span className={styles.lineFieldLabel}>
                                {getAccrualFieldLabel(diff.field)}
                            </span>
                            <span className={styles.before}>
                                {
                                    describeDiffSide({
                                        refValue: diff.before,
                                        field: diff.field,
                                        currency,
                                    }).text
                                }
                            </span>
                        </li>
                    ))}
                </ul>
            )}
            {lineDetails}
        </>
    );
}

function buildUpdateContent({
    log,
    resolver,
}: {
    log: AuditLog;
    resolver: AccrualLabelResolver;
}): AccrualActivityContent[] {
    const currency = getAccrualCurrency(log);
    const { lineDiff, fieldDiffs } = splitDiffs(log);
    const fieldPhrases = fieldDiffs
        .map((diff) => renderFieldPhrase({ diff, currency }))
        .filter((phrase) => phrase !== null);
    const linePhrases = lineDiff
        ? describeLineDeltas({ diff: lineDiff, currency, resolver })
        : [];

    if (linePhrases.length > 1) {
        const listedPhrases = linePhrases.slice(0, MAX_LISTED_LINE_ROWS);
        const hiddenCount = linePhrases.length - listedPhrases.length;
        const entries: AccrualActivityContent[] = [];
        if (fieldPhrases.length > 0) {
            entries.push({ summary: <>{joinPhrases(fieldPhrases)}.</> });
        }
        listedPhrases.forEach((phrase, index) => {
            const isLastListedRow = index === listedPhrases.length - 1;
            entries.push({
                summary: <>{phrase}.</>,
                details:
                    isLastListedRow && hiddenCount > 0 ? (
                        <>
                            and{" "}
                            {pluralizeWithCount(
                                "more line item change",
                                hiddenCount,
                            )}
                        </>
                    ) : undefined,
            });
        });
        return entries;
    }

    const phrases: React.ReactNode[] = [...fieldPhrases];
    if (linePhrases.length === 1) {
        phrases.push(linePhrases[0]);
    } else if (lineDiff) {
        phrases.push(<>updated the line items</>);
    }

    if (phrases.length === 0) {
        return [{ summary: <>made an update.</> }];
    }

    return [{ summary: <>{joinPhrases(phrases)}.</> }];
}

export function buildAccrualActivityContent({
    log,
    entityLabel,
    resolver,
}: {
    log: AuditLog;
    entityLabel: AccrualEntityLabel;
    resolver: AccrualLabelResolver;
}): AccrualActivityContent[] {
    // Reference-only event with no diff — the invoice email send job logs
    // this the moment the email is delivered, so the sentence is fixed rather
    // than derived from a before/after snapshot.
    if (
        log.sourceType === SQSProcessName.INVOICE_NATIVE_EMAIL_SEND &&
        log.diff.length === 0
    ) {
        return [{ summary: <>sent the {entityLabel} email.</> }];
    }

    if (log.diff.some((d) => d.field === ACCRUAL_MATCH_AUDIT_FIELD)) {
        return [buildMatchContent({ entityLabel, log })];
    }

    if (log.changeType === AuditLogChangeType.CREATE) {
        return [{ summary: <>created this {entityLabel}.</> }];
    }

    if (log.changeType === AuditLogChangeType.DELETE) {
        return [
            {
                summary: <>deleted this {entityLabel}.</>,
                details: renderDeletedSnapshotDetails({ log, resolver }),
            },
        ];
    }

    return buildUpdateContent({ log, resolver });
}
